import { api } from '@/lib/http-client';
import type {
  ApiKeyStatus,
  ChannelCredentials,
  ChannelName,
  ChannelSummary,
  IntegrationsOverview,
  ProviderName,
  TestResult,
} from '@/types/integrations';

/**
 * Módulo 2 — Integraciones (`docs/openapi.yaml` → tag `Integrations`).
 *
 * Reglas del backend que esta capa respeta tal cual:
 *  - las API keys __nunca se devuelven en claro__: solo `{ configurada, preview }`;
 *  - guardar/borrar/probar es solo ADMIN (el resumen general lo ve cualquier miembro);
 *  - `test` siempre responde 200 y el veredicto viaja en `{ ok, message }`
 *    (un 400 aquí es credenciales con forma inválida, no "no conectó").
 */

/** `GET /api/tenants/:tenantId/integrations` — estado de las API keys y de los canales. */
export function fetchIntegrationsOverview(tenantId: string): Promise<IntegrationsOverview> {
  return api.get<IntegrationsOverview>(`/api/tenants/${tenantId}/integrations`);
}

/** `GET /api/tenants/:tenantId/integrations/channels` — resumen de canales conectados. */
export function listChannels(tenantId: string): Promise<ChannelSummary[]> {
  return api.get<ChannelSummary[]>(`/api/tenants/${tenantId}/integrations/channels`);
}

/** `PUT /api/tenants/:tenantId/integrations/:provider` (solo ADMIN; la key se guarda cifrada). */
export function setProviderKey(
  tenantId: string,
  provider: ProviderName,
  apiKey: string,
): Promise<ApiKeyStatus> {
  return api.put<ApiKeyStatus>(`/api/tenants/${tenantId}/integrations/${provider}`, { apiKey });
}

/** `DELETE /api/tenants/:tenantId/integrations/:provider` (solo ADMIN). */
export function removeProviderKey(
  tenantId: string,
  provider: ProviderName,
): Promise<{ configurada: boolean }> {
  return api.delete<{ configurada: boolean }>(
    `/api/tenants/${tenantId}/integrations/${provider}`,
  );
}

/**
 * `POST /api/tenants/:tenantId/integrations/:provider/test` (solo ADMIN).
 * Sin `apiKey` en el body prueba la que está guardada.
 */
export function testProviderConnection(
  tenantId: string,
  provider: ProviderName,
  apiKey?: string,
): Promise<TestResult> {
  return api.post<TestResult>(
    `/api/tenants/${tenantId}/integrations/${provider}/test`,
    apiKey ? { apiKey } : {},
  );
}

/**
 * `PUT /api/tenants/:tenantId/integrations/channels/:channel` (solo ADMIN).
 * Conecta (o reconecta) el canal validando las credenciales del canal y guardándolas cifradas.
 */
export function connectChannel(
  tenantId: string,
  channel: ChannelName,
  credenciales: ChannelCredentials,
): Promise<ChannelSummary> {
  return api.put<ChannelSummary>(
    `/api/tenants/${tenantId}/integrations/channels/${channel}`,
    { credenciales },
  );
}

/** `DELETE /api/tenants/:tenantId/integrations/channels/:channel` (solo ADMIN). */
export function disconnectChannel(
  tenantId: string,
  channel: ChannelName,
): Promise<{ disconnected: boolean }> {
  return api.delete<{ disconnected: boolean }>(
    `/api/tenants/${tenantId}/integrations/channels/${channel}`,
  );
}

/**
 * `POST /api/tenants/:tenantId/integrations/channels/:channel/test` (solo ADMIN).
 * Prueba contra la Graph API de Meta con las credenciales guardadas o con las del body.
 */
export function testChannel(
  tenantId: string,
  channel: ChannelName,
  credenciales?: ChannelCredentials,
): Promise<TestResult> {
  return api.post<TestResult>(
    `/api/tenants/${tenantId}/integrations/channels/${channel}/test`,
    credenciales ? { credenciales } : {},
  );
}
